import type { AgentAbout, CopyResult, Gallery, Notification } from '@ralph/contracts'
import { apiGet, apiSend } from './client.ts'

/**
 * The consumer marketplace endpoints (PRD-18 phase 1).
 *
 * Every call goes through `api()` so the CSRF header and the canonical error envelope are
 * handled once. Nothing here validates the response against the Zod schema on the way in:
 * the contract is enforced server-side, and re-parsing a large gallery on every keystroke
 * would cost more than it catches. The types are the contract's own, so a shape change in
 * `packages/contracts/src/marketplace.ts` still breaks the build here.
 */

/**
 * `NotificationListSchema` exists in the contracts but is not exported as a type; the shape
 * is restated here rather than inferred, and the element type is imported so a change to a
 * notification still fails the typecheck.
 */
export type NotificationList = { items: Notification[]; unread: number }

export type GalleryQuery = { search?: string; category?: string }

/**
 * The gallery with its groups rail always present.
 *
 * Home and chat spread `sharedWithYourGroups` while they render. An API still on the release
 * before PRD-26 — mid-rollout, or rolled back without this app — names that rail `byYourTeam`
 * and sends no `sharedWithYourGroups` at all, and spreading `undefined` crashed the whole page
 * (D-66). Either name is read, and neither is an empty rail.
 */
export const withGroupsRail = (gallery: Gallery): Gallery => {
  const sent = gallery as Partial<Pick<Gallery, 'sharedWithYourGroups' | 'byYourTeam'>>
  return { ...gallery, sharedWithYourGroups: sent.sharedWithYourGroups ?? sent.byYourTeam ?? [] }
}

export const fetchGallery = async ({ search, category }: GalleryQuery = {}): Promise<Gallery> => {
  // Filtering is server-side on purpose (AGM-006): ranking is deterministic and defined once
  // in SQL, and a client-side filter over a page of cards would quietly disagree with it.
  const params = new URLSearchParams()
  if (search) params.set('search', search)
  if (category) params.set('category', category)
  const query = params.toString()
  return withGroupsRail(await apiGet<Gallery>(`/api/marketplace/agents${query ? `?${query}` : ''}`))
}

export const fetchAgentAbout = (agentId: string): Promise<AgentAbout> =>
  apiGet<AgentAbout>(`/api/marketplace/agents/${encodeURIComponent(agentId)}`)

/** Records that this viewer has seen the first-use notice for one capability class (AGM-009). */
export const acknowledgeNotice = (agentId: string, capability: 'actions' | 'memory'): Promise<void> =>
  apiSend<void>(`/api/marketplace/agents/${encodeURIComponent(agentId)}/notices`, 'POST', { capability })

export const copyAgent = (agentId: string): Promise<CopyResult> =>
  apiSend<CopyResult>(`/api/marketplace/agents/${encodeURIComponent(agentId)}/copy`, 'POST')

export const fetchNotifications = (unreadOnly = false): Promise<NotificationList> =>
  apiGet<NotificationList>(`/api/notifications${unreadOnly ? '?unread=true' : ''}`)

export const markNotificationRead = (id: string): Promise<{ read: boolean }> =>
  apiSend<{ read: boolean }>(`/api/notifications/${encodeURIComponent(id)}/read`, 'POST')

export const markAllNotificationsRead = (): Promise<{ read: number }> =>
  apiSend<{ read: number }>('/api/notifications/read-all', 'POST')
